import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { LoginErrorState } from '@interfaces';

const SERVER_ERROR_MIN = 500;
const SERVER_ERROR_MAX = 599;

/**
 * HTTP status of a failed request, or `null` when the error is not an HTTP
 * response at all. Status `0` (network failure, CORS, offline) is kept as 0.
 */
export function httpErrorStatus(err: unknown): number | null {
  if (err instanceof HttpErrorResponse) {
    return err.status;
  }
  return null;
}

/** Network failures (status 0) and any 5xx are transient. */
export function isTemporaryError(err: unknown): boolean {
  const status = httpErrorStatus(err);
  if (status === null) {
    return false;
  }
  return (
    status === 0 || (status >= SERVER_ERROR_MIN && status <= SERVER_ERROR_MAX)
  );
}

/**
 * Maps a failed sign-in to the state the login screen renders. Decided by
 * the HTTP status only: the response body (often HTML on a gateway error, or
 * a `detail` that names the account) is never read nor surfaced.
 * Unknown shapes fall back to `generic` — never a fabricated reason.
 */
export function mapLoginError(err: unknown): LoginErrorState {
  if (httpErrorStatus(err) === HttpStatusCode.BadRequest) {
    return 'credentials';
  }
  if (isTemporaryError(err)) {
    return 'temporary';
  }
  return 'generic';
}

/** Recovery for an unknown email answers 404; the UI treats it as sent. */
export function isRecoveryNotFound(err: unknown): boolean {
  return httpErrorStatus(err) === HttpStatusCode.NotFound;
}
